/**
 * Game Detection Service
 * Communicates with backend to scan for installed games
 */

import { getModsForGame } from './modIndexService';

const LOCAL_DAEMON = import.meta.env.VITE_LOCAL_DAEMON_URL || 'http://localhost:41933/api';

/**
 * Scan PC for installed games
 * @returns {Promise<{success: boolean, games: Array, stats: Object}>}
 */
export async function scanForGames() {
    try {
        const response = await fetch(`${LOCAL_DAEMON}/games/scan`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Backend error: ${response.status}`);
        }

        const data = await response.json();

        // Enrich games with mod information
        if (data.success && data.games) {
            data.games = enrichGamesWithMods(data.games);
        }

        return data;
    } catch (error) {
        console.error('Failed to scan for games:', error);

        // Check if backend is running
        if (error.message.includes('fetch')) {
            throw new Error('Backend server nicht erreichbar. Bitte starten Sie den Backend-Server.');
        }

        throw error;
    }
}

/**
 * Enrich detected games with mod availability information
 * @param {Array} detectedGames - Array of games from backend scan
 * @returns {Array} Array of games with mod information
 */
export function enrichGamesWithMods(detectedGames) {
    return detectedGames.map(game => {
        // Get available mods for this game
        const availableMods = getModsForGame(game.id);

        return {
            ...game,
            mods: availableMods.length,
            hasModsAvailable: availableMods.length > 0,
            modCategories: getUniqueCategories(availableMods),
            popularModsCount: availableMods.filter(mod => mod.popularityScore >= 80).length
        };
    });
}

/**
 * Get unique categories from mod array
 * @param {Array} mods - Array of mod objects
 * @returns {Array} Array of unique category names
 */
function getUniqueCategories(mods) {
    const categories = new Set();
    mods.forEach(mod => categories.add(mod.category));
    return Array.from(categories);
}

/**
 * Check if backend is available
 * @returns {Promise<boolean>}
 */
export async function checkBackendHealth() {
    try {
        const response = await fetch(`${LOCAL_DAEMON}/health`);
        const data = await response.json();
        return data.status === 'ok';
    } catch (error) {
        return false;
    }
}

